import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useNotification from "../../hooks/useNotification";
import useViewer from "../../hooks/useViewer";
import useTranslations from "../../hooks/useTranslations";
import { InsertGroupDialog } from "../groups/InsertGroupDialog";
import { CheckIcon, CloseIcon, EditIcon, SettingsIcon } from "../icons";
import SearchPopover from "../utils/SearchPopover";
import SearchPopoverItem from "../utils/SearchPopoverItem";
import SearchPopoverHeading from "../utils/SearchPopoverHeading";
import { Button, H4 } from "../wombat-ui";
import GroupTag from "../groups/GroupTag";
import { isEmpty, trim } from "lodash";
import { GetUserDocument, GroupFragment, useGetGroups, useInsertUser, UserReviewFragment } from '../../graphql/generated/definitions';

const GroupSearchItem: React.FC<{
  group: GroupFragment;
  emphasized: boolean;
  baseId?: string;
  canRemove: boolean;
}> = ({ group, emphasized, baseId, canRemove }) => (
  <SearchPopoverItem id={`${baseId}${group.id}`}>
    <div className="flex space-x-2">
      {emphasized && <CheckIcon className="w-5 h-5 text-primary-500" />}
      <p>{group.name}</p>
    </div>
    {emphasized && canRemove && (
      <div>
        <CloseIcon className="w-3 h-3 text-dark-100" />
        <span className="sr-only">Remove group</span>
      </div>
    )}
  </SearchPopoverItem>
);

interface UserGroupsProps {
  user: UserReviewFragment;
}

const UserGroups: React.FC<UserGroupsProps> = ({ user }) => {
  const t = useTranslations();

  const {
    data: groupsData,
    loading: groupsLoading,
    refetch: refetchGroups,
  } = useGetGroups({ variables: { sortBy: "name" } });
  const groups = groupsData?.groups?.collection || [];
  const { notifySuccess } = useNotification();
  const [groupModal, setGroupModal] = useState({
    open: false,
    selectedGroup: undefined,
  });

  const [updateUserGroups] = useInsertUser({
    refetchQueries: [{ query: GetUserDocument, variables: { id: user.id } }],
  });

  const handleItemSelected = useCallback(
    (group: GroupFragment, emphasized: boolean) => {
      let newGroupsIds: string[];

      if (!emphasized)
        newGroupsIds = [...user.groups, group].map(({ id }) => id);
      else
        newGroupsIds = user.groups
          .filter(({ id }) => id !== group.id)
          .map(({ id }) => id);

      updateUserGroups({
        variables: {
          input: {
            id: user.id,
            groupIds: newGroupsIds,
          },
        },
      }).then(() =>
        notifySuccess({
          title: t("userProfile.notification.success") as string,
        })
      );
    },
    [updateUserGroups, user]
  );

  const [search, setSearch] = useState("");
  useEffect(() => {
    refetchGroups({ q: search });
  }, [search]);

  const { isCapableOfInsertingGroups } = useViewer();

  const filteredGroups = useMemo(
    () =>
      groups
        .filter(
          ({ id: groupId }) =>
            !user.groups.find(
              ({ id: userGroupId }) => groupId === userGroupId
            )
        ),
    [user, groups]
  );

  const emphasizedElements = useMemo(
    () => (isEmpty(search) ? user.groups : []),
    [user.groups, search]
  );

  // We don't want to show the "Add XXX" button
  // when we have an exact match.
  const isMatchExact = useMemo(
    () => groups.find(({ name }) => name === trim(search)),
    [search, groups]
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <H4>{t("collaborator.groups")}</H4>
        {user.canInsert && (
          <SearchPopover
            title={
              <SearchPopoverHeading>
                {t("collaborator.groups") as string}
              </SearchPopoverHeading>
            }
            placeholder={t("collaborator.filterGroups") as string}
            loading={groupsLoading}
            items={filteredGroups}
            align="bottom-right"
            emphasized={[{ elements: emphasizedElements }]}
            onItemSelected={handleItemSelected}
            onSearchChange={setSearch}
            searchValue={search}
            renderItem={(item: GroupFragment, emphasized: boolean) => (
              <GroupSearchItem
                group={item}
                emphasized={emphasized}
                baseId={user.id}
                canRemove={item.canDestroy}
              />
            )}
            button={
              <Button variant="ghost" icon={SettingsIcon}>
                <span className="sr-only">{t("groups.changeUserGroups")}</span>
              </Button>
            }
            noMatch={
              !isCapableOfInsertingGroups && (
                <SearchPopoverItem>
                  {t("wombat.select.emptyResult")}
                </SearchPopoverItem>
              )
            }
            footer={
              isCapableOfInsertingGroups && (
                <ul className={"sticky bottom-0 divide-y"}>
                  {!isEmpty(search) && !isMatchExact && (
                    <li>
                      <SearchPopoverItem
                        className="cursor-pointer"
                        onClick={() =>
                          setGroupModal({
                            selectedGroup: undefined,
                            open: true,
                          })
                        }
                        id="addGroupButton"
                      >
                        {t("collaborator.addGroup", { groupName: search })}
                      </SearchPopoverItem>
                    </li>
                  )}
                  <li>
                    <Link to="/groups">
                      <SearchPopoverItem>
                        {t("collaborator.editGroups")}
                        <EditIcon className="w-4 h-4" />
                      </SearchPopoverItem>
                    </Link>
                  </li>
                </ul>
              )
            }
          />
        )}
      </div>
      {user.groups.length === 0 && (
        <p className="text-sm text-light-900">{t("collaborator.noGroups")}</p>
      )}
      <div className="flex flex-wrap -mx-1.5 py-2">
        {user.groups.map((group: GroupFragment) => (
          <div className="px-1.5 py-1" key={group.id}>
            <GroupTag group={group} />
          </div>
        ))}
      </div>
      <InsertGroupDialog
        open={groupModal.open}
        group={groupModal.selectedGroup}
        defaultName={search}
        onClose={() => setGroupModal({ selectedGroup: undefined, open: false })}
      />
    </div>
  );
};

export default UserGroups;
